import React, { useState, useEffect, useContext } from 'react'
import {
  Button,
  Divider,
  BodyText,
  CaptionText,
  Link,
  LoadingSpinner,
  Card,
  WarningIcon,
} from '@paypalcorp/pp-react'
import PropTypes from 'prop-types'
import {
  Message,
  Context as WorldReadyContext,
} from '@paypalcorp/worldready-react'
import Header from './components/header'
import apiClient from '../api'
import { MessageFormat } from '@paypalcorp/worldready'
import { errorSetter, extractTokenFromUrl, removeHeaderFooter } from './utils'
import fpti from '../instrumentation'

function AccountAuth(props) {
  const worldReady = useContext(WorldReadyContext)
  const [merchantName, setMerchantName] = useState('')
  const [loading, setLoading] = useState(true)
  const loginPageData = props.loginPageInfo
  const [logInBtnState, setLogInBtnState] = useState('initial')
  const [cancelUrl, setCancelUrl] = useState(null)
  const [error, setError] = useState(null)
  const [signInUrl, setSignInUrl] = useState()

  useEffect(() => {
    document.title = new MessageFormat(worldReady, {
      id: 'authLogin.loginWithPaypal',
    }).format()
    removeHeaderFooter(document)
    const merchantText = new MessageFormat(worldReady, {
      id: `authLogin.merchantUpper`,
    }).format()
    const urlToken = extractTokenFromUrl(window)
    apiClient
      .get(`/getMerchantDetails?token=${urlToken}`)
      .then((response) => {
        setError(false)
        const merchantDetails = response.data
        const merchantNameFromResponse =
          merchantDetails.merchantName || merchantText
        setMerchantName(merchantNameFromResponse)
        const cancelUrlFromUrl = new URL(merchantDetails.cancelUrl)
        cancelUrlFromUrl.searchParams.append('token', urlToken)
        setCancelUrl(cancelUrlFromUrl)
        setSignInUrl(merchantDetails.signInUrl)
        setLoading(false)
        return null
      })
      .catch((e) => {
        fpti.logFPTIError(
          e,
          `Get merchant details error ${e.error.error_code}:${e.error.error_message}`,
          e,
        )
        if (e.error.error_code === '11009') {
          errorSetter(e, urlToken, merchantText, setCancelUrl, setMerchantName)
        }
        const errorMessage = `${new MessageFormat(worldReady, {
          id: `authLogin.${e.message}`,
        }).format()} ${e.error.error_code}`
        setLoading(false)
        setError(errorMessage)
      })
  }, [worldReady])

  const redirectToLogin = async () => {
    setLogInBtnState('processing')
    window.location.href = signInUrl
  }

  if (error) {
    return (
      <div className="aa_alertIndex_parent" id="aa_error">
        <WarningIcon size="xl" color="sysColorErrorMain" />
        <BodyText className="aa_errorText" type="error">
          {error}
        </BodyText>
        {cancelUrl && (
          <BodyText className="aa_smallcaption">
            <Message id="authLogin.returnTo" merchantName={merchantName} />{' '}
            <Link href={cancelUrl}>{merchantName}</Link>
          </BodyText>
        )}
      </div>
    )
  }

  return (
    <div data-testid="home-page" className="aa_root">
      {loading && (
        <LoadingSpinner
          className="aa_rootLoading"
          screenReaderText="loading"
          size="xl"
        />
      )}
      {!loading && (
        <Card card className="card_style">
          <Card.Header className="card_header">
            <Header />
          </Card.Header>
          <Divider className="pp_divider" />
          <Card.Content className="card_content" align="center">
            <div className="aa_text">
              <BodyText type="strong" className="aa_strongText">
                <Message id={`authLogin.${loginPageData}.headerText`} />
              </BodyText>
              <BodyText>
                <Message
                  id={`authLogin.${loginPageData}.bodyText`}
                  merchantName={merchantName}
                />
              </BodyText>
            </div>
            <CaptionText className="aa_caption">
              <Message
                id={`authLogin.${loginPageData}.captionText`}
                merchantName={merchantName}
              />
            </CaptionText>
            <div className="aa_btnAndLink">
              <Button
                className="aa_redirectButton"
                onClick={redirectToLogin}
                btnState={logInBtnState}
                id="aa_LoginButton"
              >
                <Message id="authLogin.logIn" merchantName={merchantName} />
              </Button>
              <BodyText className="aa_smallcaption">
                <Message id="authLogin.returnTo" merchantName={merchantName} />{' '}
                <Link href={cancelUrl}>{merchantName}</Link>
              </BodyText>
            </div>
          </Card.Content>
        </Card>
      )}
    </div>
  )
}

AccountAuth.propTypes = {
  loginPageInfo: PropTypes.string,
}

export default AccountAuth
